/**
 * Content Enrichment Agent
 * Enriches listing data with additional content and metadata
 */

import jinaAI from '../jinaAI';

interface EnrichedContent {
  description: string;
  longDescription: string;
  highlights: string[];
  products: string[];
  specialties: string[];
  hours: any;
  amenities: string[];
  certifications: string[];
}

class ContentEnrichmentAgent {
  /**
   * Enrich listing with web-scraped data
   */
  async enrichListing(listing: any): Promise<EnrichedContent> {
    let enrichedContent: EnrichedContent = {
      description: listing.description || '',
      longDescription: '',
      highlights: [],
      products: [],
      specialties: [],
      hours: listing.hours || {},
      amenities: listing.tags || [],
      certifications: [],
    };

    // Scrape website if available
    if (listing.website) {
      const businessInfo = await jinaAI.scrapeBusinessInfo(listing.website);

      if (businessInfo) {
        enrichedContent.longDescription = businessInfo.description;
        enrichedContent.products = [...enrichedContent.products, ...businessInfo.products];

        if (businessInfo.hours && Object.keys(businessInfo.hours).length > 0) {
          enrichedContent.hours = { ...enrichedContent.hours, ...businessInfo.hours };
        }
      }
    }

    // Extract highlights from description
    enrichedContent.highlights = this.extractHighlights(enrichedContent.description);

    // Identify certifications
    enrichedContent.certifications = this.identifyCertifications(
      enrichedContent.description + ' ' + enrichedContent.longDescription
    );

    // Extract specialties
    enrichedContent.specialties = this.extractSpecialties(listing);

    return enrichedContent;
  }

  /**
   * Extract highlights from text
   */
  private extractHighlights(text: string): string[] {
    const highlights: string[] = [];

    // Common highlight keywords
    const keywords = [
      'organic',
      'certified',
      'fresh',
      'local',
      'sustainable',
      'family-owned',
      'award-winning',
      'seasonal',
      'non-gmo',
      'pesticide-free',
      'farm-to-table',
      'artisan',
    ];

    keywords.forEach(keyword => {
      const regex = new RegExp(`\\b${keyword}\\b`, 'i');
      if (regex.test(text)) {
        highlights.push(keyword.charAt(0).toUpperCase() + keyword.slice(1));
      }
    });

    return [...new Set(highlights)];
  }

  /**
   * Identify certifications
   */
  private identifyCertifications(text: string): string[] {
    const certifications: string[] = [];

    const certPatterns = [
      { pattern: /usda\s*organic/i, name: 'USDA Organic' },
      { pattern: /certified\s*organic/i, name: 'Certified Organic' },
      { pattern: /non[\s-]?gmo/i, name: 'Non-GMO' },
      { pattern: /gmo[\s-]?free/i, name: 'GMO-Free' },
      { pattern: /fair\s*trade/i, name: 'Fair Trade' },
      { pattern: /florida\s*certified/i, name: 'Florida Certified' },
      { pattern: /naturally\s*grown/i, name: 'Naturally Grown' },
    ];

    certPatterns.forEach(({ pattern, name }) => {
      if (pattern.test(text)) {
        certifications.push(name);
      }
    });

    return [...new Set(certifications)];
  }

  /**
   * Extract specialties from listing
   */
  private extractSpecialties(listing: any): string[] {
    const specialties: string[] = [];

    // Check product types
    if (listing.products) {
      if (Array.isArray(listing.products)) {
        specialties.push(...listing.products.slice(0, 5));
      } else if (typeof listing.products === 'object') {
        if (listing.products.vegetables?.length > 0) specialties.push('Fresh Vegetables');
        if (listing.products.fruit?.length > 0) specialties.push('Fresh Fruit');
        if (listing.products.herbs?.length > 0) specialties.push('Fresh Herbs');
        if (listing.products.specialty_items?.length > 0) specialties.push('Specialty Items');
      }
    }

    // Check tags
    if (listing.tags) {
      const specialtyTags = listing.tags.filter((tag: string) =>
        ['U-Pick Available', 'Farm Tours', 'Online Ordering', 'Delivery'].includes(tag)
      );
      specialties.push(...specialtyTags);
    }

    return [...new Set(specialties)].slice(0, 6);
  }

  /**
   * Generate SEO-friendly description
   */
  generateSEODescription(listing: any, enrichedContent: EnrichedContent): string {
    const parts: string[] = [];

    // Business name and type
    parts.push(`${listing.name} is a ${listing.type.toLowerCase()} located in ${listing.city}, Florida.`);

    // Highlights
    if (enrichedContent.highlights.length > 0) {
      parts.push(`Known for ${enrichedContent.highlights.slice(0, 3).join(', ').toLowerCase()} products.`);
    }

    // Specialties
    if (enrichedContent.specialties.length > 0) {
      parts.push(`Specializing in ${enrichedContent.specialties.slice(0, 3).join(', ').toLowerCase()}.`);
    }

    // Certifications
    if (enrichedContent.certifications.length > 0) {
      parts.push(`${enrichedContent.certifications.join(', ')} certified.`);
    }

    return parts.join(' ');
  }

  /**
   * Generate keywords for SEO
   */
  generateKeywords(listing: any, enrichedContent: EnrichedContent): string[] {
    const keywords: string[] = [];

    // Location keywords
    keywords.push(listing.city);
    keywords.push(`${listing.city} ${listing.type}`);
    keywords.push(`Southwest Florida ${listing.type}`);
    keywords.push(`SWFL ${listing.type}`);

    // Product keywords
    if (enrichedContent.specialties.length > 0) {
      enrichedContent.specialties.forEach(specialty => {
        keywords.push(`${specialty} ${listing.city}`);
      });
    }

    // Certification keywords
    enrichedContent.certifications.forEach(cert => {
      keywords.push(`${cert} ${listing.city}`);
    });

    // Highlight keywords
    enrichedContent.highlights.forEach(highlight => {
      keywords.push(`${highlight} ${listing.type} ${listing.city}`);
    });

    return [...new Set(keywords)].slice(0, 20);
  }

  /**
   * Batch enrich multiple listings
   */
  async batchEnrich(listings: any[]): Promise<Map<string, EnrichedContent>> {
    const results = new Map<string, EnrichedContent>();

    for (const listing of listings) {
      try {
        const enriched = await this.enrichListing(listing);
        results.set(listing.id, enriched);

        // Rate limiting
        await new Promise(resolve => setTimeout(resolve, 1500));
      } catch (error) {
        console.error(`Error enriching ${listing.name}:`, error);
      }
    }

    return results;
  }
}

export default new ContentEnrichmentAgent();
